import { useEffect, useRef } from 'react';

// Attach the returned ref to an empty div at the bottom of a scroll
// container to fetch more once it scrolls into view. Reads pagination state
// through a ref rather than the effect's own deps so the observer is set up
// once and doesn't get torn down/recreated on every fetch.
const useInfiniteScrollSentinel = (
  hasNextPage: boolean,
  isFetchingNextPage: boolean,
  fetchNextPage: () => void
) => {
  const sentinelRef = useRef<HTMLDivElement>(null);
  const stateRef = useRef({ hasNextPage, isFetchingNextPage, fetchNextPage });
  stateRef.current = { hasNextPage, isFetchingNextPage, fetchNextPage };
  const isIntersectingRef = useRef(false);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel || typeof IntersectionObserver === 'undefined') return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        isIntersectingRef.current = entry.isIntersecting;
        const { hasNextPage, isFetchingNextPage, fetchNextPage } = stateRef.current;
        if (entry.isIntersecting && hasNextPage && !isFetchingNextPage) fetchNextPage();
      },
      { threshold: 0.1 }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, []);

  // The observer above only fires on a geometry change (scroll/resize), so if a
  // loaded page doesn't fill the container, the sentinel stays intersecting and
  // the observer never fires again once hasNextPage flips true. Re-check on every
  // pagination state change to keep loading until the container actually overflows.
  useEffect(() => {
    if (isIntersectingRef.current && hasNextPage && !isFetchingNextPage) fetchNextPage();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  return sentinelRef;
};

export default useInfiniteScrollSentinel;
